import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, X, Sparkles, ChevronRight, Footprints, HeartHandshake, MessagesSquare, Megaphone, Users, Radio, Building2, GraduationCap, Trophy, Shield, Rocket, ShoppingBag, Medal } from "lucide-react";
import { toast } from "sonner";
import { superaxApi, apiError } from "../lib/api";

const ICONS = {
  Footprints, HeartHandshake, MessagesSquare, Megaphone, Users, Radio, Building2,
  GraduationCap, Whistle: Medal, Trophy, Shield, Rocket, ShoppingBag,
};

export default function SuperaX() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    superaxApi.list().then(setData).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!data) return <div className="min-h-screen bg-cream grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-[#E9A400]" /></div>;

  return (
    <div className="min-h-screen bg-cream pb-40" data-testid="superax-page">
      {/* Header */}
      <header className="bg-navy sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button data-testid="superax-back" onClick={() => navigate("/mi-cuenta")} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold">
            <ArrowLeft className="w-5 h-5" /> Volver
          </button>
          <img src="/logo-supera-light.png" alt="Supera" className="h-9 w-auto object-contain" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Hero card */}
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A1C30] via-[#12314f] to-[#1c3e5f] p-6 sm:p-8 text-white">
          <span className="absolute -top-12 -right-8 w-52 h-52 rounded-full bg-[#E9A400]/15 blur-3xl" />
          <span className="absolute -bottom-16 -left-10 w-56 h-56 rounded-full bg-[#2F5BEA]/15 blur-3xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 ring-1 ring-white/15 px-3 py-1 text-[11px] font-bold tracking-widest text-[#E9A400]">
              <Sparkles className="w-3.5 h-3.5" /> EXPERIENCIAS SUPERA
            </span>
            <h1 className="mt-3 text-5xl sm:text-6xl font-extrabold leading-none">
              Supera <span className="italic text-[#E9A400]">X</span>
            </h1>
            <p className="mt-3 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">
              {data.intro}
            </p>
          </div>
        </section>

        {/* Options grid — mobile first (2 cols) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mt-5">
          {data.items.map((it) => {
            const Icon = ICONS[it.icono] || Sparkles;
            const color = it.color || "#E9A400";
            return (
              <button
                key={it.id}
                data-testid={`superax-item-${it.slug}`}
                onClick={() => setActive(it)}
                className="group relative text-left rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-4 sm:p-5 overflow-hidden transition-all hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] min-h-[9rem]"
                style={it.imagen_url ? { backgroundImage: `url(${it.imagen_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
              >
                {it.imagen_url ? (
                  <>
                    <span className="absolute inset-0 bg-gradient-to-t from-[#0A1C30]/90 via-[#0A1C30]/35 to-transparent" />
                    <span className="relative flex flex-col justify-end h-full min-h-[7rem]">
                      <span className="block text-sm sm:text-base font-extrabold text-white leading-tight drop-shadow-lg">{it.titulo}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-white/80 mt-1">
                        Ver más <ChevronRight className="w-3.5 h-3.5" />
                      </span>
                    </span>
                  </>
                ) : (
                  <>
                    <span className="absolute -right-6 -top-6 w-20 h-20 rounded-full opacity-10 blur-lg transition-opacity group-hover:opacity-20" style={{ backgroundColor: color }} />
                    <span className="relative flex flex-col gap-3">
                      <span className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}1A` }}>
                        <Icon className="w-6 h-6" style={{ color }} />
                      </span>
                      <span className="block text-sm sm:text-base font-extrabold text-[#0A1C30] leading-tight min-h-[2.5rem]">{it.titulo}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#9AA3B2] group-hover:text-[#0A1C30] transition-colors">
                        Ver más <ChevronRight className="w-3.5 h-3.5" />
                      </span>
                    </span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail bottom-sheet / modal */}
      {active && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={() => setActive(null)}>
          <div className="absolute inset-0 bg-[#0A1C30]/50 backdrop-blur-sm" />
          <div
            className="relative w-full sm:max-w-lg bg-white ring-1 ring-black/5 rounded-t-3xl sm:rounded-3xl p-6 pb-8 shadow-2xl animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
            data-testid="superax-detail"
          >
            <div className="mx-auto sm:hidden w-10 h-1 rounded-full bg-black/10 mb-4" />
            <button onClick={() => setActive(null)} className="absolute top-4 right-4 text-[#9AA3B2] hover:text-[#0A1C30]" data-testid="superax-close"><X className="w-5 h-5" /></button>
            {(() => {
              const Icon = ICONS[active.icono] || Sparkles;
              const color = active.color || "#E9A400";
              return (
                <>
                  <span className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${color}1A` }}>
                    <Icon className="w-8 h-8" style={{ color }} />
                  </span>
                  <h2 className="mt-4 text-2xl font-extrabold text-[#0A1C30]">{active.titulo}</h2>
                  {active.resumen && <p className="mt-1 text-sm font-semibold" style={{ color }}>{active.resumen}</p>}
                  <div className="mt-3 text-[#4A5568] text-sm leading-relaxed whitespace-pre-wrap">
                    {active.descripcion
                      ? active.descripcion
                      : "Estamos preparando algo increíble para ti. Muy pronto vivirás esta experiencia Supera. ✨"}
                  </div>
                  {active.slug === "carrera-mensual" && (
                    <button
                      data-testid="race-start-cta"
                      onClick={() => navigate("/carrera")}
                      className="mt-5 w-full py-4 rounded-2xl bg-gradient-to-r from-[#E9A400] to-[#f0b62e] text-[#0A1C30] text-base font-extrabold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl active:scale-[0.98] transition-all"
                    >
                      <Footprints className="w-5 h-5" /> Inicia tu carrera ahora
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
